import type { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import type { DeepReadonly, Plugin } from 'vue';

import type { Locale } from '@/config/build/index.js';

import type { AbstractClient } from '../abstracts';
import type { NoaIntegrationRequestConfig, SSORequestConfig } from '../definitions';
import type { createState } from '../state';

export enum REQUEST {
  BASE = 'base',
  SSO = 'sso',
  NOA_INTEGRATIONS = 'noa_integrations',
}

export const ENV = {
  production: 'production',
  development: 'development',
  testing: 'testing',
} as const;

export type Environment = (typeof ENV)[keyof typeof ENV];

export interface GlobalConfig {
  locale: Locale;
  env: Environment;
  testingName?: string;
}

export interface PluginOptions {
  [REQUEST.BASE]?: AxiosRequestConfig;
  [REQUEST.SSO]?: SSORequestConfig;
  [REQUEST.NOA_INTEGRATIONS]?: NoaIntegrationRequestConfig;
  globalConfig: GlobalConfig;
}

export interface RequestConfig {
  token?: string | undefined | (() => string | undefined);
  baseURL?: string;
}

export type RequestPlugin = {
  config: DeepReadonly<PluginOptions>;
  state: DeepReadonly<ReturnType<typeof createState>>;
  onError: (clb: Record<number, (error: AxiosError) => void>) => void;
} & Plugin;

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export type ConstructorType<T> = new (...args: any[]) => T;

type PossibleFunction<R, E> = ((entry: E) => R) | R;

export type RequestDefinition<C, O, E> = PossibleFunction<RequestDefinitionOptions<C, O, E>, AxiosInstance>;

export type RequestExtend<O, E> = PossibleFunction<RequestExtendOptions<O, E>, AxiosInstance>;

interface RequestExtendOptions<O, E> {
  options: PossibleFunction<O, E>;
}

interface RequestDefinitionOptions<C, O, E> {
  id: `${REQUEST}`;
  client: ConstructorType<C & AbstractClient<O>>;
  options: PossibleFunction<O, E>;
}
